import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layouts/DashboardLayout';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../context/LanguageContext';

// ── helpers ──────────────────────────────────────────────────────────────────
const getInitials = (name = '') => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return (name[0] || '?').toUpperCase();
};

const avatarColors = ['#6366f1', '#8b5cf6', '#f59e0b', '#10b981', '#3b82f6', '#ef4444'];
const pickColor = (name = '') => avatarColors[name.charCodeAt(0) % avatarColors.length];

// ── sub-components ────────────────────────────────────────────────────────────
const Card = ({ children, style = {} }) => (
    <div style={{
        background: 'white',
        borderRadius: 20,
        padding: '6px 0',
        boxShadow: '0 2px 16px rgba(0,0,0,0.07)',
        border: '1px solid rgba(0,0,0,0.05)',
        overflow: 'hidden',
        ...style,
    }}>
        {children}
    </div>
);

const Row = ({ icon, iconBg, label, sublabel, right, onClick, danger }) => (
    <button
        onClick={onClick}
        disabled={!onClick}
        style={{
            display: 'flex', alignItems: 'center', gap: 14,
            padding: '14px 20px', width: '100%',
            background: 'transparent', border: 'none',
            cursor: onClick ? 'pointer' : 'default',
            textAlign: 'left',
            transition: 'background 0.15s',
        }}
        onMouseEnter={e => { if (onClick) e.currentTarget.style.background = danger ? '#fef2f2' : '#f8fafc'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
        <div style={{
            width: 44, height: 44, borderRadius: 13, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: iconBg || '#f1f5f9', fontSize: 22,
        }}>
            {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: danger ? '#ef4444' : '#1e293b', lineHeight: 1.3 }}>
                {label}
            </div>
            {sublabel && (
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{sublabel}</div>
            )}
        </div>
        {right && <div style={{ flexShrink: 0 }}>{right}</div>}
        {onClick && !right && (
            <span style={{ color: '#cbd5e1', fontSize: 20 }}>›</span>
        )}
    </button>
);

const Divider = () => (
    <div style={{ height: 1, background: '#f1f5f9', margin: '0 20px' }} />
);

const SectionLabel = ({ children }) => (
    <div style={{ padding: '20px 4px 8px', fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>
        {children}
    </div>
);

// ── main component ────────────────────────────────────────────────────────────
const SettingsPage = () => {
    const navigate = useNavigate();
    const { lang, toggleLang } = useLanguage();

    const [userInfo, setUserInfo] = useState({
        fullName: localStorage.getItem('userFullName') || '',
        email: '',
        level: 'Flyers',
        joinedDays: 0,
    });
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

    // Try to enrich user info from API
    useEffect(() => {
        const token = localStorage.getItem('accessToken');
        if (!token) return;
        fetch(`${process.env.REACT_APP_API_URL}/users/me`, {
            headers: { Authorization: `Bearer ${token}`, accept: 'application/json' },
        })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (data) {
                    const name = data.full_name || data.email || userInfo.fullName;
                    localStorage.setItem('userFullName', name);
                    setUserInfo(prev => ({
                        ...prev,
                        fullName: name,
                        email: data.email || '',
                    }));
                }
            })
            .catch(() => { });
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('userFullName');
        navigate('/login');
    };

    const initials = getInitials(userInfo.fullName || userInfo.email);
    const avatarColor = pickColor(userInfo.fullName || userInfo.email);
    const displayName = userInfo.fullName || userInfo.email || 'Người dùng';
    const firstName = displayName.trim().split(/\s+/).pop();

    const isVi = lang === 'vi';
    const otherLang = isVi ? 'en' : 'vi';

    const T = {
        title: isVi ? 'Cài Đặt' : 'Settings',
        accountSection: isVi ? 'Tài Khoản' : 'Account',
        generalSection: isVi ? 'Cài Đặt Chung' : 'General',
        hello: isVi ? `Xin chào, ${firstName}! 👋` : `Hello, ${firstName}! 👋`,
        tagline: isVi ? 'Tiếp tục chinh phục Flyers nhé 🚀' : 'Keep conquering Flyers! 🚀',
        level: isVi ? 'Trình độ' : 'Level',
        language: isVi ? 'Ngôn Ngữ' : 'Language',
        languageSub: isVi ? 'Chọn ngôn ngữ hiển thị' : 'Choose display language',
        currentLang: translations[lang].langFlag + ' ' + translations[lang].langLabel,
        logout: isVi ? 'Đăng Xuất' : 'Log Out',
        logoutSub: isVi ? 'Thoát khỏi tài khoản hiện tại' : 'Sign out of your account',
        confirmTitle: isVi ? 'Đăng xuất?' : 'Log out?',
        confirmMsg: isVi ? 'Bạn có chắc muốn đăng xuất không?' : 'Are you sure you want to log out?',
        cancel: isVi ? 'Huỷ' : 'Cancel',
        version: isVi ? 'Phiên bản' : 'Version',
    };

    return (
        <DashboardLayout>
            {/* Same wrapper as Dashboard: flex-1 overflow-y-auto p-4 md:p-8 */}
            <div className="flex-1 overflow-y-auto p-4 md:p-8"
                style={{ background: 'linear-gradient(180deg, #f5f3ff 0%, #eef2ff 40%, #f0fdf4 100%)' }}
            >
                {/* Same inner container as Dashboard: max-w-6xl mx-auto */}
                <div className="max-w-6xl mx-auto">

                    {/* Header */}
                    <div style={{
                        padding: '28px 24px 24px',
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        borderRadius: 24,
                        marginBottom: 8,
                        position: 'relative',
                        overflow: 'hidden',
                    }}>
                        {/* Decorative circles */}
                        <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
                        <div style={{ position: 'absolute', bottom: -20, left: 60, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />

                        <h1 style={{ margin: '0 0 20px', fontSize: 22, fontWeight: 900, color: 'white', position: 'relative' }}>
                            ⚙️ {T.title}
                        </h1>

                        {/* Avatar card */}
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 16,
                            background: 'rgba(255,255,255,0.15)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: 20,
                            padding: '16px 20px',
                            border: '1px solid rgba(255,255,255,0.2)',
                            position: 'relative',
                        }}>
                            {/* Avatar circle */}
                            <div style={{
                                width: 64, height: 64, borderRadius: '50%',
                                background: avatarColor,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 26, fontWeight: 900, color: 'white',
                                border: '3px solid rgba(255,255,255,0.4)',
                                boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                                flexShrink: 0,
                            }}>
                                {initials}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 18, fontWeight: 900, color: 'white', marginBottom: 3 }}>
                                    {displayName}
                                </div>
                                {userInfo.email && userInfo.email !== userInfo.fullName && (
                                    <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', marginBottom: 6 }}>
                                        {userInfo.email}
                                    </div>
                                )}
                                <div style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 5,
                                    background: 'rgba(255,255,255,0.2)', borderRadius: 8,
                                    padding: '3px 10px',
                                }}>
                                    <span style={{ fontSize: 14 }}>🎓</span>
                                    <span style={{ fontSize: 12, fontWeight: 700, color: 'white' }}>{T.level}: {userInfo.level}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div>

                        {/* General section */}
                        <SectionLabel>{T.generalSection}</SectionLabel>

                        <Card>
                            {/* Language toggle */}
                            <Row
                                icon={translations[lang].langFlag}
                                iconBg="#eef2ff"
                                label={T.language}
                                sublabel={T.languageSub}
                                onClick={toggleLang}
                                right={
                                    <div style={{
                                        display: 'flex', alignItems: 'center',
                                        background: '#f1f5f9', borderRadius: 10,
                                        padding: '3px', gap: 2,
                                    }}>
                                        {['vi', 'en'].map(l => (
                                            <button
                                                key={l}
                                                onClick={(e) => { e.stopPropagation(); if (lang !== l) toggleLang(); }}
                                                style={{
                                                    padding: '5px 12px', borderRadius: 8, border: 'none',
                                                    fontSize: 12, fontWeight: 700, cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    background: lang === l ? 'white' : 'transparent',
                                                    color: lang === l ? '#6366f1' : '#94a3b8',
                                                    boxShadow: lang === l ? '0 2px 6px rgba(0,0,0,0.12)' : 'none',
                                                }}
                                            >
                                                {translations[l].langFlag} {l.toUpperCase()}
                                            </button>
                                        ))}
                                    </div>
                                }
                            />
                        </Card>

                        {/* Account section */}
                        <SectionLabel>{T.accountSection}</SectionLabel>

                        <Card>
                            {/* Logout */}
                            <Row
                                icon="🚪"
                                iconBg="#fef2f2"
                                label={T.logout}
                                sublabel={T.logoutSub}
                                onClick={() => setShowLogoutConfirm(true)}
                                danger
                            />
                        </Card>

                        {/* App version footer */}
                        <div style={{ textAlign: 'center', marginTop: 32, color: '#cbd5e1', fontSize: 12 }}>
                            <div>🦋 Flyers Prep</div>
                            <div style={{ marginTop: 4 }}>{T.version} 1.0.0</div>
                        </div>
                    </div>
                </div> {/* end max-w-6xl */}
            </div> {/* end flex-1 */}

            {/* Logout confirm modal */}
            {showLogoutConfirm && (
                <div
                    onClick={() => setShowLogoutConfirm(false)}
                    style={{
                        position: 'fixed', inset: 0, zIndex: 2000,
                        background: 'rgba(0,0,0,0.45)',
                        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
                        animation: 'fadeIn 0.2s ease',
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: 'white',
                            borderRadius: '24px 24px 0 0',
                            padding: '24px 24px 40px',
                            width: '100%',
                            maxWidth: 480,
                            animation: 'slideUpModal 0.3s cubic-bezier(0.34,1.56,0.64,1)',
                        }}
                    >
                        <div style={{ width: 40, height: 5, borderRadius: 3, background: '#e2e8f0', margin: '0 auto 20px' }} />
                        <div style={{ textAlign: 'center', marginBottom: 24 }}>
                            <div style={{ fontSize: 48, marginBottom: 12 }}>🚪</div>
                            <div style={{ fontSize: 20, fontWeight: 800, color: '#1e293b', marginBottom: 8 }}>{T.confirmTitle}</div>
                            <div style={{ fontSize: 14, color: '#64748b' }}>{T.confirmMsg}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 12 }}>
                            <button
                                onClick={() => setShowLogoutConfirm(false)}
                                style={{
                                    flex: 1, padding: '14px',
                                    borderRadius: 14, border: '2px solid #e2e8f0',
                                    background: 'white', fontSize: 15, fontWeight: 700,
                                    color: '#64748b', cursor: 'pointer',
                                }}
                            >
                                {T.cancel}
                            </button>
                            <button
                                onClick={handleLogout}
                                style={{
                                    flex: 1, padding: '14px',
                                    borderRadius: 14, border: 'none',
                                    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                                    boxShadow: '0 6px 16px rgba(239,68,68,0.35)',
                                    fontSize: 15, fontWeight: 800,
                                    color: 'white', cursor: 'pointer',
                                }}
                            >
                                {T.logout}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUpModal { from { transform: translateY(100%); } to { transform: translateY(0); } }
      `}</style>
        </DashboardLayout>
    );
};

export default SettingsPage;
